import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ErrorMessage, PageLoader, Spinner } from '@/components/ui'
import { ApiError, api } from '@/lib/api'
import { formatDateTime } from '@/lib/format'
import {
  AUDIT_ACTION_TITLES,
  type AuditLogListResponse,
  type Workspace,
  type WorkspaceListResponse,
} from '@/lib/types'
import { useActiveWorkspace, useAuthStore, useHasPermission } from '@/stores/auth'

/** Таймзоны, актуальные для России и СНГ. Полный список IANA пользователю не нужен. */
const TIMEZONES = [
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Samara',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
  'Asia/Almaty',
  'Asia/Tashkent',
  'Europe/Minsk',
  'Asia/Yerevan',
]

const CURRENCIES = ['RUB', 'USD', 'EUR', 'KZT', 'BYN', 'UZS', 'AMD', 'KGS']

const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
]

export function SettingsPage() {
  const workspace = useActiveWorkspace()
  const setWorkspaces = useAuthStore((state) => state.setWorkspaces)
  const canManage = useHasPermission('workspace:manage')
  const canViewAudit = useHasPermission('audit:view')
  const queryClient = useQueryClient()

  const workspaceId = workspace?.workspace_id

  const details = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => api.get<Workspace>(`/workspaces/${workspaceId}`),
    enabled: Boolean(workspaceId),
  })

  const [form, setForm] = useState<Partial<Workspace>>({})
  const value = { ...details.data, ...form } as Workspace | undefined

  const save = useMutation({
    mutationFn: () =>
      api.patch<Workspace>(`/workspaces/${workspaceId}`, {
        name: form.name,
        timezone: form.timezone,
        currency: form.currency,
        fiscal_year_start_month: form.fiscal_year_start_month,
        week_start: form.week_start,
      }),
    onSuccess: async () => {
      setForm({})
      void queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] })
      // Название могло измениться — обновляем переключатель пространств
      const list = await api.get<WorkspaceListResponse>('/workspaces', { skipWorkspace: true })
      setWorkspaces(
        list.items.map((item) => ({
          workspace_id: item.id,
          name: item.name,
          slug: item.slug,
          role: item.role,
          permissions: item.permissions,
        })),
      )
    },
  })

  const audit = useQuery({
    queryKey: ['audit-logs', workspaceId],
    queryFn: () => api.get<AuditLogListResponse>('/audit-logs?limit=30'),
    enabled: Boolean(workspaceId) && canViewAudit,
  })

  if (!workspace || details.isLoading) return <PageLoader />

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : {}
  const hasChanges = Object.keys(form).length > 0

  return (
    <div className="space-y-6">
      <section className="card p-6">
        <h2 className="text-base font-semibold text-slate-900">Настройки пространства</h2>
        <p className="mt-1 text-sm text-slate-500">
          Таймзона, начало финансового года и начало недели определяют, как считаются периоды в
          отчётах. От них зависит, сойдутся ли цифры с вашей учётной системой.
        </p>

        <ErrorMessage error={details.error} />

        {value && (
          <form
            className="mt-5 grid gap-4 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault()
              save.mutate()
            }}
          >
            <div className="sm:col-span-2">
              <label className="label" htmlFor="ws-name">
                Название
              </label>
              <input
                id="ws-name"
                className={`input ${fieldErrors.name ? 'input-error' : ''}`}
                value={value.name}
                disabled={!canManage}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>

            <div>
              <label className="label" htmlFor="ws-timezone">
                Таймзона
              </label>
              <select
                id="ws-timezone"
                className="input"
                value={value.timezone}
                disabled={!canManage}
                onChange={(event) => setForm((prev) => ({ ...prev, timezone: event.target.value }))}
              >
                {/* Текущее значение может быть вне списка, если задано через API */}
                {!TIMEZONES.includes(value.timezone) && (
                  <option value={value.timezone}>{value.timezone}</option>
                )}
                {TIMEZONES.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="ws-currency">
                Валюта
              </label>
              <select
                id="ws-currency"
                className="input"
                value={value.currency}
                disabled={!canManage}
                onChange={(event) => setForm((prev) => ({ ...prev, currency: event.target.value }))}
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="ws-fiscal">
                Начало финансового года
              </label>
              <select
                id="ws-fiscal"
                className="input"
                value={value.fiscal_year_start_month}
                disabled={!canManage}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    fiscal_year_start_month: Number(event.target.value),
                  }))
                }
              >
                {MONTHS.map((month, index) => (
                  <option key={month} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="label" htmlFor="ws-week">
                Начало недели
              </label>
              <select
                id="ws-week"
                className="input"
                value={value.week_start}
                disabled={!canManage}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, week_start: Number(event.target.value) }))
                }
              >
                <option value={1}>понедельник</option>
                <option value={7}>воскресенье</option>
              </select>
            </div>

            <div className="sm:col-span-2">
              <ErrorMessage error={save.error} />
            </div>

            {canManage && (
              <div className="flex gap-2 sm:col-span-2">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={save.isPending || !hasChanges}
                >
                  {save.isPending && <Spinner className="h-4 w-4" />}
                  Сохранить
                </button>
                {hasChanges && (
                  <button type="button" className="btn-secondary" onClick={() => setForm({})}>
                    Отменить
                  </button>
                )}
              </div>
            )}

            {!canManage && (
              <p className="text-xs text-slate-500 sm:col-span-2">
                Изменять настройки может только владелец пространства.
              </p>
            )}
          </form>
        )}
      </section>

      {canViewAudit && (
        <section className="card overflow-hidden">
          <header className="border-b border-slate-100 px-6 py-4">
            <h2 className="text-base font-semibold text-slate-900">Журнал действий</h2>
            <p className="mt-1 text-xs text-slate-500">
              Последние события пространства. Журнал не редактируется из интерфейса.
            </p>
          </header>

          {audit.isLoading && <PageLoader />}
          <div className="px-6">
            <ErrorMessage error={audit.error} />
          </div>

          {audit.data && (
            <ul className="divide-y divide-slate-100">
              {audit.data.items.map((entry) => (
                <li key={entry.id} className="flex items-baseline gap-4 px-6 py-3 text-sm">
                  <time className="tabular w-36 shrink-0 text-xs text-slate-400">
                    {formatDateTime(entry.created_at)}
                  </time>
                  <span className="text-slate-800">
                    {AUDIT_ACTION_TITLES[entry.action] ?? entry.action}
                  </span>
                  {entry.ip && (
                    <span className="ml-auto font-mono text-xs text-slate-400">{entry.ip}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  )
}
